/**
 * CreatorGmvSection — admin-only card showing Phyllo's estimated GMV
 * range for the creator. Commercially sensitive; admin-gated at the
 * call site in AnalyticsTab.
 *
 * Phyllo returns `creator_gmv` at the doc root (sibling of `profile`),
 * not inside profile — this component takes the gmv object directly.
 * Reading from `profile.creator_gmv` was the original silent-render
 * bug: the field was always undefined and the section never displayed.
 */
import type { InsightIQCreatorGMV } from '@discovr/types';
import { SectionTitle } from '../charts/StatCard';
import { fmtNum } from '../utils/formatters';

interface CreatorGmvSectionProps {
  gmv: InsightIQCreatorGMV | null | undefined;
}

export function CreatorGmvSection({ gmv }: CreatorGmvSectionProps) {
  if (!gmv || !gmv.price_range) return null;
  const { min, max } = gmv.price_range;
  if (min == null && max == null) return null;

  const currency = gmv.currency || 'USD';
  let range: string;
  if (min != null && max != null) {
    range = `${currency} ${fmtNum(min)} – ${fmtNum(max)}`;
  } else if (min != null) {
    range = `${currency} ${fmtNum(min)}+`;
  } else {
    // At this point min is null and max is non-null (the earlier
    // `if (min == null && max == null) return null` guarantees one of them
    // is set).
    range = `Up to ${currency} ${fmtNum(max as number)}`;
  }

  return (
    <div className="rounded-2xl border border-border bg-card p-5 mb-4">
      <SectionTitle>Estimated Creator GMV</SectionTitle>
      <div className="mt-2 flex items-baseline gap-3">
        <div className="text-heading-h5 text-foreground">{range}</div>
        <div className="text-caption-regular text-muted-foreground">Phyllo estimate · lifetime</div>
      </div>
    </div>
  );
}
